import { AxiosResponse } from 'axios'
import qs from 'query-string'
import { z } from 'zod'

import { BASE_URL } from '../config'
import { MIME_TYPE } from '../../constant/enum/mimeType'
import { getDocsContent, postDocsContent } from '../../api/editor/editor'
import { getURLFromAPIType } from '../../function/getEditorApiEndpoint'
import noop from './../../function/noop'

import customFetch from './../../function/customFetch'
import axios from './../../function/axiosInstance'
import extract from './../../function/extract'
import { transformClientSideStage } from './transformClientSideStage'
import {
  ContentAuditReport,
  ContentAuditReportSync
} from '../../types/content_audit'

const url = `${BASE_URL}/assignments`
/*
 * * query management
 */
export const createThread = async ({
  id,
  type,
  text,
  attachments
}: {
  id: string
  type: ThreadType
  text: string
  attachments: string[]
}): Promise<ApiResponse> => {
  try {
    const response = await axios.post(`/assignments/${id}/threads`, {
      type,
      text,
      attachments
    })
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const fetchThreads = async ({
  id,
  type
}: {
  id: string
  type?: string
}): Promise<GetThreadsResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }
  try {
    const queryParamsThreadApi = type ? `type=${type}` : ''
    const response = await customFetch(
      `${url}/${id}/threads?${queryParamsThreadApi}`,
      { headers }
    )
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.threads,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: [],
      error: error.message
    }
  }
}

export const fetchMentions = async ({
  id
}: {
  id: string
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/${id}/mention-suggestions`, {
      headers
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.data,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const createMessage = async ({
  id,
  text,
  attachments
}: {
  id: string
  text: string
  attachments: string[]
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${BASE_URL}/threads/${id}/messages`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ text, attachments })
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.data,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

const STAGE_PROGRESS = ['editorial_review', 'client_review', 'creator_rework']
/*
 * * assignments
 */
// ? * Improvement: We can make use of same functions for project & assignment by moving the types to the react query hooks and these api functions with have generic object types, so typechecking will happen at hook level instead of here
export const fetchList = async ({
  query_params = {},
  isPaginatedList = true,
  page = 0,
  page_size = 100,
  isV2 = false
}: {
  query_params?: Partial<AssignmentQueryParams>
  isPaginatedList?: boolean
  page?: number
  page_size?: number | undefined
  isV2?: boolean
}): Promise<GetAssignmentList> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }

  // adding query params
  const reqQueryParams = new URLSearchParams()
  if (isPaginatedList) {
    reqQueryParams.append('current_page', page.toString())
    reqQueryParams.append('page_size', page_size.toString())
  }
  Object.keys(query_params).forEach(key => {
    const queryParam = query_params[key]
    // Logic to handle stage query param containing 'editorial_review' or 'client_review' key as they are not supported by backend
    if (key === 'stage') {
      const listParams = []
      if (!Array.isArray(queryParam)) {
        listParams.push(queryParam)
      } else {
        listParams.push(...queryParam)
      }
      const stages: AssignmentStage[] = []
      const statuses: string[] = []

      listParams.forEach(clientSideStage => {
        if (STAGE_PROGRESS.includes(clientSideStage as string)) {
          statuses.push(clientSideStage as string)
          stages.push('completed')
        } else {
          stages.push(clientSideStage as AssignmentStage)
        }
      })

      if (stages.length > 0) {
        reqQueryParams.set('stage', stages.join(','))
      }
      if (statuses.length > 0) {
        reqQueryParams.set('stage_progress', statuses.join(','))
      }
      // adding query param only if it has valid value (undefined or null or empty string or [])
    } else if (queryParam?.toString()) {
      reqQueryParams.set(key, queryParam.toString())
    }
  })
  try {
    const _url = isV2 ? `${BASE_URL}/items` : url
    const response = await customFetch(`${_url}?${reqQueryParams.toString()}`, {
      headers
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: {
          records: result.records,
          total_records: result.total_records
        },
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: {
        records: [],
        total_records: 0
      },
      error: error.message
    }
  }
}

export const fetchDetails = async ({
  id,
  companyId,
  includeActivityLog = false
}: {
  id: string
  companyId?: string
  includeActivityLog?: boolean
}): Promise<GetAssignmentDetails> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }
  try {
    const endpoint = qs.stringifyUrl(
      {
        url: `${url}/${id}`,
        query: {
          company_id: companyId,
          ...(includeActivityLog && {
            include_activity_log: includeActivityLog
          })
        }
      },
      { skipNull: true, skipEmptyString: true }
    )

    const response = await customFetch(endpoint, { headers })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: undefined,
      error: error.message
    }
  }
}

export const editDetails = async ({
  id,
  body
}: {
  id: string
  body: Record<string, unknown>
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const editDeadline = async ({
  id,
  body
}: {
  id: string
  body: Record<string, unknown>
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/${id}/deadline`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const updateBulkAssignmentStatusV2 = async (
  body: UpdateAssignmentStatusParams[]
): Promise<ApiResponseV2<BulkUpdateResponse>> => {
  try {
    const response = await axios.put('/assignments/bulk-status', {
      assignments_data: body
    })
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getBulkTransactionStatus = async (
  id: string
): Promise<ApiResponseV2<BulkTransactionResponse>> => {
  try {
    const response = await axios.get(`/assignments/transactions/${id}`)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const resolveAssignmentStatus = async (body: {
  assignment_ids: string[]
  project_id: string
}): Promise<ApiResponse> => {
  try {
    const response = await axios.post('/assignments/resolve', body)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const unScrapAssignmentStatus = async (body: {
  assignment_ids: string[]
  project_id: string
  deadline: string
}): Promise<ApiResponse> => {
  try {
    const response = await axios.post('/assignments/unscrapped', body)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getWordsLength = (input: string): number => {
  let result = input.replace(/(^\s*)|(\s*$)/gi, '')
  result = result.replace(/ {2,}/gi, ' ')
  result = result.replace(/\n /, '\n')

  if (result === '') return 0

  return result.split(' ').length
}

const _getLocalContent = async (assignmentId: string) => {
  const localAssignmentString = localStorage.getItem(
    `assignment_data_${assignmentId}`
  )

  if (!localAssignmentString) {
    return null
  }

  const response = await getDocsContent({
    id: assignmentId
  })

  const localAssignment = JSON.parse(localAssignmentString)
  const isLocalContentNewer =
    localAssignment?.body?.extras?.createdAt > response?.data?.extras?.createdAt

  localStorage.removeItem(`assignment_data_${assignmentId}`)
  if (!response?.data?.extras?.createdAt || isLocalContentNewer) {
    return localAssignment?.body
  }

  return null
}

export const updateAssignmentContent = async (
  assignmentId: string
): Promise<ApiResponse> => {
  const updatedLocalData = await _getLocalContent(assignmentId)
  // Silently ignore if there's no content in localstorage
  if (!updatedLocalData)
    return {
      status: 'ok',
      data: null,
      error: undefined
    }

  const response = await postDocsContent({
    id: assignmentId,
    body: updatedLocalData
  })

  if (response.data && !response.error) {
    return {
      status: 'ok',
      data: response.data,
      error: undefined
    }
  } else {
    return {
      status: 'error',
      data: null,
      error: 'Unable to sync editor content.'
    }
  }
}

export const updateAssignmentStatus = async ({
  id,
  body
}: {
  id: string
  body: UpdateAssignmentStatusParams
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/${id}/status`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: { result, id },
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const updateAssignmentFeedback = async ({
  id,
  body
}: {
  id: string
  body: SubmitFeedback
}): Promise<ApiResponse> => {
  try {
    const response = await axios.post(`${url}/${id}/creator-feedback`, body)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getContentAuditStatus = async ({
  assignmentId,
  wordCount,
  stateId,
  apiType
}: {
  assignmentId: string
  wordCount?: number
  stateId?: string
  apiType?: string
}): Promise<AuditData> => {
  const queryParams = qs.stringify({ state_id: stateId, word_count: wordCount })
  try {
    const auditUrl = `/${getURLFromAPIType(
      apiType
    )}/${assignmentId}/content-audit?${queryParams}`

    const response = await axios.get(auditUrl)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const postContentAuditStatus = async (
  async: boolean,
  assignmentId: string,
  params?: {
    get_plagiarism_score?: boolean
    get_readability_score?: boolean
    get_fact_check_report?: boolean
    get_dos_and_donts_report?: boolean
    get_outline_report?: boolean
    word_count?: number
    content?: {
      html: string
      text: string
    }
  },
  apiType?: string
): Promise<ApiResponse<ContentAuditReport | ContentAuditReportSync>> => {
  let endpoint = `/${getURLFromAPIType(apiType)}/${assignmentId}/content-audit`
  if (async) endpoint += '?async=true'

  try {
    const response = await axios.post(endpoint, {
      ...(params ?? {})
    })
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const uploadOpenSubmissionFiles = async ({
  id,
  body
}: {
  id: string
  body: { extras: string[]; content?: string }
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/${id}/submission-documents`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const redactAssignment = async ({
  id
}: {
  id: string
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/${id}/redact`, {
      method: 'PUT',
      headers
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: response,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const updateDeadline = async ({
  id,
  deadline
}: {
  id: string
  deadline: string
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/${id}/deadline`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ deadline })
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: response,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const fetchWorkflow = async (id: string) => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/${id}/workflow`, { headers })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export type AssignmentFeedback = {
  feedback_text: string
  rating: 0 | 1 | 2 | 3 | 4 | 5
  assignment_id: string
}

export const submitBulkFeedback = async (
  feedback: AssignmentFeedback[]
): Promise<ApiResponseV2<{ status: 'success' }>> => {
  try {
    const response = await axios.post(`${url}/feedback`, {
      assignments_feedback: feedback
    })
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const createFeedback = async ({
  assignment_id = '',
  rating = 1,
  feedback_text = '',
  inputs = [],
  compliments = []
}: {
  assignment_id: string
  rating: number
  feedback_text: string
  inputs?: string[]
  compliments?: string[]
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/${assignment_id}/feedback`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ rating, feedback_text, inputs, compliments })
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.data,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const handleAcceptAssignment = async ({
  showMessage = noop,
  assignment_id,
  body
}: {
  showMessage?: (parameter: any) => void | undefined
  assignment_id: string
  body: {
    assigner_role: string
    assigner_id: string
    assignee_id: string
    assignment_id: string
    reason: string
    rework_areas: string[]
    prev_assignee_id?: null | string
    prev_assigne_pay?: null | number
    status: AssignmentStatus
  }
}): Promise<{ isSuccess: boolean; message: string }> => {
  try {
    const response = await updateAssignmentStatus({
      id: assignment_id,
      body
    })
    if (response.status === 'ok') {
      const message = 'Assignment(s) successfully accepted.'
      showMessage({ type: 'success', message })
      return { isSuccess: true, message }
    } else {
      throw new Error(response.error)
    }
  } catch (error) {
    const message =
      error.message || 'Unknown error occurred. Try again after some time.'
    showMessage({ type: 'error', message })
    return { isSuccess: false, message }
  }
}

export const editAssigneeDetails = async ({
  id,
  body
}: {
  id: string
  body: { assignee_pay: PayScale['amount'] }
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/${id}/assignee-details`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const downloadSubmissionFile = async ({
  id,
  format = 'pdf'
}: {
  id: string
  format?: string
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }
  const fileType = MIME_TYPE[format].type
  try {
    const response = await customFetch(
      `${url}/${id}/doc?action=download&format=${fileType}`,
      {
        headers
      }
    )
    const result = await response.blob()
    const blob = new Blob([result], {
      type: fileType
    })
    const file = new File([blob], `Assignment.${format}`, {
      type: fileType
    })
    if (response.ok) {
      return {
        status: 'ok',
        data: file,
        error: undefined
      }
    }
    const jsonResult = await response.json()
    throw new Error(jsonResult.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const getAssignmentSubmissionFileURL = async (
  assignmentId: string
): Promise<ApiResponse> => {
  try {
    const response = await axios.get(`/assignments/${assignmentId}/doc-url`)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const makeThreadSeen = async ({
  assignmentId
}: {
  assignmentId: string
}): Promise<ApiResponse> => {
  try {
    const response = await axios.put(
      `/assignments/${assignmentId}/threads/message-receipts`,
      { read: 1 }
    )
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const addBulkAssignmentPay = async ({
  body
}: {
  body: {
    project_id: string
    assignment_ids: string[]
    assignment_amount: PayScale
  }
}): Promise<ApiResponse> => {
  try {
    const response = await axios.put(`/assignments/amount`, body)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const updateBulkAssignments = async (
  payload: unknown
): Promise<ApiResponse> => {
  try {
    const response = await axios.put(`/assignments`, payload)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

//** new opportunities assignments apis
export const activeOpportunitiesAssignmentDetails = ({
  user_Id,
  query_params = {},
  page = 0,
  page_size = 5
}: {
  user_Id: string
  query_params?: Partial<UserQueryParams>
  page?: number
  page_size?: number
}): Promise<any> => {
  const reqQueryParams = new URLSearchParams()
  reqQueryParams.append('current_page', page.toString())
  reqQueryParams.append('page_size', page_size.toString())
  Object.keys(query_params).forEach(key => {
    const queryParam = query_params[key]
    // adding query param only if it has valid value (undefined or null or empty string or [])
    if (queryParam?.toString()) {
      reqQueryParams.set(key, queryParam.toString())
    }
  })

  return axios
    .get<AxiosResponse>(`/assignments?${reqQueryParams.toString()}`, {
      params: {
        'active-allotment': true,
        user_id: user_Id,
        stage: 'request'
      }
    })
    .then((data: AxiosResponse<any>) => {
      return data
    })
    .catch(error => error)
}

export const missedOpportunitiesAssignmentDetails = (
  key: string,
  user_Id: string,
  page_size = 10
): Promise<AssignmentLists> => {
  return axios
    .get(
      `/assignments?missed-allotment=true&user_id=${user_Id}&page_size=${page_size}&current_page=0`
    )
    .then((data: AxiosResponse<AssignmentLists>) => {
      return data.data
    })
    .catch(error => error)
}

export const getNewOpportunitiesCount = async (): Promise<ApiResponse> => {
  try {
    const response = await axios.get(`/opportunities/new-count`)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}
export const showInterestInAssignment = async (
  dispatch_id: string
): Promise<ApiResponse> => {
  try {
    const response = await axios.put(`/opportunities/${dispatch_id}`, {
      data: {
        showed_interest: true
      }
    })
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}
type Feedback = {
  dispatch_id: string
  flag: boolean
}
export const relevanceForAssignment = async ({
  dispatch_id,
  flag
}: Feedback): Promise<ApiResponse> => {
  try {
    const response = await axios.put(`/opportunities/${dispatch_id}`, {
      data: {
        missed: {
          is_relevant: flag
        }
      }
    })
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

//** Get feedback types
export const getFeedbackTypes = (): Promise<any> => {
  return axios
    .get<any>(`/opportunities/dispatch-feedback-types`)
    .then((data: AxiosResponse<any>) => data.data.data)
    .catch(error => error)
}

type IPassOpportunity = {
  dispatch_id?: string
  id: string | number | string[] | number[]
  isGroup?: boolean
  groupId?: string | null
}

//** Claim and Pass new opportunities
export const passOpportunity = async ({
  dispatch_id,
  id,
  isGroup,
  groupId
}: IPassOpportunity): Promise<ApiResponse> => {
  try {
    let response
    if (isGroup) {
      response = await axios.post(`/opportunities/pass-by-group/${groupId}`, {
        feedback: id
      })
    } else {
      response = await axios.post(`/opportunities/pass/${dispatch_id}`, {
        feedback: id
      })
    }

    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

type IClaimOpportunity = {
  dispatch_id: string
  deadline: string
  assignee_pay?: any
  assignment_id: string
  high_pay?: boolean
}

export const claimOpportunity = async ({
  dispatch_id,
  deadline,
  assignee_pay,
  assignment_id,
  high_pay
}: IClaimOpportunity): Promise<ApiResponse> => {
  try {
    const response = await axios.post(`/opportunities/claim/${dispatch_id}`, {
      deadline,
      assignee_pay,
      assignment_id,
      high_pay
    })
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getFlaggedCreatorDetails = async (
  assignmentId: string
): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `/assignments/${assignmentId}/creator-flags`
    )
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const updateAssignmentInvoicingStatus = async ({
  assignmentIds,
  invoicingStatus
}: {
  assignmentIds: string[]
  invoicingStatus:
    | 'pending'
    | 'ready'
    | 'approved'
    | 'sent'
    | 'paid'
    | 'paid_offline'
    | 'rejected'
    | 'error'
    | 'on_hold'
    | 'non_invoiceable'
}): Promise<ApiResponse> => {
  try {
    const response = await axios.put(
      `/assignments/invoice-status/${invoicingStatus}`,
      { assignment_ids: assignmentIds }
    )
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

// for bulk assignments operation
export const getAssignmentsMargin = async (
  assignmentIds: string[]
): Promise<ApiResponseV2<AssignmentMarginDataType>> => {
  try {
    const response = await axios.post(`/assignments/allotment-margin`, {
      assignment_ids: assignmentIds
    })

    const result = extract.data(response)

    return result
  } catch (error) {
    return extract.error(error)
  }
}

export const getAssigneeMargin = async (
  assignmentIds: string[],
  amount: number
) => {
  try {
    const request = await axios.post<{ margin_percentage: string }>(
      '/assignments/allotment-margin-by-assignee',
      { assignment_ids: assignmentIds, flat_rate_pay: amount }
    )
    return extract.data(request)
  } catch (error) {
    return extract.error(error)
  }
}

interface PastAllotmentsArgs {
  assignmentIds: string[]
  checkIfAllotted?: boolean
}

export const pastAllotments = async ({
  assignmentIds,
  checkIfAllotted
}: PastAllotmentsArgs): Promise<ApiResponseV2<PastAllotment[]>> => {
  try {
    const payload = {
      assignment_ids: assignmentIds,
      check_if_allotted: checkIfAllotted
    }

    const response = await axios.post('/assignments/past-allotments', payload)

    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

interface PastAllotmentsByIdArgs {
  pastSelectionId: string
  assignmentId: string
  companyId: string
}

export const pastAllotmentsById = async ({
  pastSelectionId,
  assignmentId,
  companyId
}: PastAllotmentsByIdArgs): Promise<ApiResponseV2<UserList[]>> => {
  try {
    const endpoint = qs.stringifyUrl(
      {
        url: `/assignments/past-allotments/${pastSelectionId}`,
        query: {
          fetch_margin: true,
          populate_is_favorite: true,
          assignment_id: assignmentId,
          company_id: companyId
        }
      },
      { skipEmptyString: true, skipNull: true }
    )
    const response = await axios.get(endpoint)

    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const batchUpdateAssignmentInvoicingStatus = async ({
  payload
}: {
  payload: {
    id: number
    status: string
  }[]
}): Promise<ApiResponse> => {
  try {
    const response = await axios.put(`/assignments/invoice-status/batch`, {
      assignments: payload
    })
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.response?.data?.data
    }
  }
}

const Schema = z.object({
  word_count: z.number(),
  total_asst_count: z.number(),
  claimed_asst_count: z.number()
})

type StatsResponse = Zod.infer<typeof Schema>

export const getAssignmentGroupStats = async ({
  groupID,
  assignmentId
}: {
  groupID: string
  assignmentId: string
}) => {
  z.string().parse(groupID)
  try {
    const response = await axios.get(
      `/assignments/${assignmentId}/allotment-group/${groupID}/stats`
    )

    Schema.parse(response.data?.data)

    return extract.data<StatsResponse>(response)
  } catch (error) {
    return extract.error(error)
  }
}
